import { useEffect, useState, useCallback } from 'react';
import { api } from '../../lib/api.js';
import { REFERRAL_TIERS } from '../../lib/referralTiers.js';

// Fallback milestones used when Beehiiv hasn't returned a program (config
// missing, API error, or new publication). Matches the visual the page shipped
// with so the page still renders sensibly without live data.
const FALLBACK_MILESTONES = REFERRAL_TIERS.map((t) => ({
  id: `local-${t.count}`,
  numReferrals: t.count,
  reward: { name: t.label, imageUrl: null },
  icon: t.icon,
  unlocked: false,
}));

function copyToClipboard(text) {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  const el = document.createElement('textarea');
  el.value = text;
  el.style.position = 'fixed';
  el.style.opacity = '0';
  document.body.appendChild(el);
  el.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(el);
  }
  return Promise.resolve();
}

// Mirrors the section-header pattern in BriefArticleSidebar.jsx — mono eyebrow
// in the bordered #FAFAFA bar, then content below on white.
function PanelHeader({ eyebrow, title }) {
  return (
    <div className="shrink-0 px-4 py-3 border-b border-black bg-[#FAFAFA]">
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#737373] font-bold">
        {eyebrow}
      </div>
      {title && (
        <div className="font-sans text-[13px] font-bold text-black mt-1">{title}</div>
      )}
    </div>
  );
}

export default function Referrals() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/me/referrals');
      setData(res);
    } catch (err) {
      setError(err?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const milestones = data?.milestones?.length ? data.milestones : FALLBACK_MILESTONES;
  const count = data?.referralCount ?? 0;
  const link = data?.referralUrl;
  const next = data?.nextMilestone || null;
  const progressPct = next && next.numReferrals > 0
    ? Math.min(100, Math.round((count / next.numReferrals) * 100))
    : count > 0 ? 100 : 0;

  async function handleCopy() {
    if (!link) return;
    await copyToClipboard(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  async function handleShare() {
    if (!link) return;
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: 'Hangar X Daily',
          text: 'Aviation & AAM intelligence, every morning. Subscribe with my link:',
          url: link,
        });
        return;
      } catch {
        // user canceled or share failed — fall through to copy
      }
    }
    await handleCopy();
  }

  return (
    <div className="max-w-page mx-auto px-4 sm:px-6 py-8 sm:py-10">
      {/* HERO — black takeover, sans display headline + tabular mono number */}
      <section className="border border-black bg-black text-white mb-6">
        <div className="px-5 sm:px-7 py-5 sm:py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div className="min-w-0">
            <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-hxd-orange font-bold m-0 mb-2">
              Refer a Friend
            </p>
            <h2 className="font-sans text-[clamp(1.25rem,3vw,1.75rem)] font-extrabold leading-tight text-white m-0">
              Share HXD. Earn Gear.
            </h2>
            <p className="font-sans text-[14px] leading-relaxed text-white/70 mt-3 mb-0 max-w-md">
              Share HXD with people in your network and unlock exclusive rewards for every person
              who subscribes using your link.
            </p>
          </div>
          <div className="text-center sm:text-right shrink-0">
            <div className="font-mono text-[clamp(3rem,6vw,4.5rem)] font-bold text-hxd-orange leading-none tabular-nums">
              {loading ? '—' : count}
            </div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/55 font-bold mt-2">
              Referrals so far
            </div>
          </div>
        </div>
      </section>

      {error && (
        <div
          role="alert"
          className="mb-5 font-sans text-[13px] text-[#c0392b] bg-[#fdecea] border border-[#f5c6cb] px-4 py-3"
        >
          Could not load referral data: {error}
        </div>
      )}

      {data?.beehiivError && (
        <div className="mb-5 font-sans text-[12px] text-[#7a5b13] bg-[#fff7e6] border border-[#fde68a] px-4 py-3">
          Live referral count is temporarily unavailable. Showing your cached number.
        </div>
      )}

      {/* LINK PANEL */}
      <div className="border border-black bg-white mb-5">
        <PanelHeader eyebrow="Your share link" title={link ? null : 'Pending Beehiiv sync'} />
        <div className="px-4 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="flex-1 min-w-0 font-mono text-[13px] text-[#0A0A0A] bg-[#FAFAFA] border border-[#D4D4D4] px-3 py-2 truncate">
            {loading ? 'Loading…' : link || 'hangarxdaily.com/ref/—'}
          </span>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={handleCopy}
              disabled={!link}
              className="hxd-btn hxd-btn-primary hxd-btn-sm"
            >
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
            <button
              onClick={handleShare}
              disabled={!link}
              className="hxd-btn hxd-btn-ghost hxd-btn-sm"
            >
              Share
            </button>
          </div>
        </div>
      </div>

      {/* PROGRESS PANEL */}
      <div className="border border-black bg-white mb-5">
        <PanelHeader
          eyebrow="Reward progress"
          title={
            next
              ? `${next.remaining} until ${next.reward?.name || 'next reward'}`
              : count > 0
                ? 'All milestones unlocked'
                : null
          }
        />
        <div className="px-4 py-4">
          <div className="h-2 bg-[#F0F0F0] border border-[#E5E5E5]">
            <div
              className="h-full bg-hxd-orange transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          {next && (
            <div className="flex items-center justify-between mt-2.5 font-mono text-[10px] tracking-[0.16em] uppercase text-[#737373] font-bold tabular-nums">
              <span>{count} referrals</span>
              <span>{next.numReferrals} goal</span>
            </div>
          )}
        </div>
      </div>

      {/* MILESTONES PANEL */}
      <div className="border border-black bg-white">
        <PanelHeader eyebrow="Milestone rewards" />
        <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {milestones.map((m) => (
            <div
              key={m.id ?? m.numReferrals}
              className={[
                'border bg-[#FAFAFA] p-4 text-center flex flex-col items-center min-h-[150px]',
                m.unlocked ? 'border-hxd-orange' : 'border-[#D4D4D4]',
              ].join(' ')}
            >
              <div className="text-2xl leading-none mb-2">
                {m.icon || (m.reward?.imageUrl ? (
                  <img src={m.reward.imageUrl} alt="" className="w-8 h-8 mx-auto object-contain" />
                ) : '🎁')}
              </div>
              <div className="font-mono text-[10px] font-bold tracking-[0.16em] uppercase text-[#737373] mb-1 tabular-nums">
                {m.numReferrals} referral{m.numReferrals === 1 ? '' : 's'}
              </div>
              <div className="font-sans text-[13px] font-bold text-black leading-snug">
                {m.reward?.name || '—'}
              </div>
              <div
                className={[
                  'font-mono text-[9px] font-bold tracking-[0.22em] uppercase mt-auto pt-2.5',
                  m.unlocked ? 'text-hxd-orange' : 'text-[#737373]',
                ].join(' ')}
              >
                {m.unlocked ? 'Unlocked' : 'Locked'}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
